// client/src/components/InventoryDisclaimer.jsx
// Three related exports:
//   - <InventoryDisclaimer /> — banner explaining inventory data is approximate.
//   - <InventoryBadge inventory={...} /> — small colored pill for product cards.
//   - <InventoryPanel product={...} onRefreshed={fn} getToken={fn} /> — the
//     full widget for product detail and presentation editor: badge, "Updated
//     Xm ago", live "Refresh stock" button, and per-color stock breakdown.
//
// Inventory data is cached and refreshed periodically (every 2 hours during
// 4am–5pm Pacific business window, weekdays). InventoryPanel lets distributors
// trigger a live SOAP call to the supplier on demand, rate-limited server-side
// to 5 minutes per product.

import React, { useMemo, useState } from 'react';
import { getInventoryDisplay, humanizeFreshness, canRefreshLive, supplierPathForRefresh, nativeProductId } from '../lib/inventory';

const COPY =
  'Stock levels shown are approximate and refreshed every 2 hours during business hours. ' +
  'Click "Refresh Stock" or "Verify with supplier" for a live check before confirming an order.';

export function InventoryDisclaimer({ compact = false, muted = false, style: extraStyle = {} }) {
  const baseStyle = compact
    ? {
        fontSize: 11,
        lineHeight: 1.4,
        color: muted ? '#6B7280' : '#1E3A8A',
        padding: muted ? '4px 0' : '6px 10px',
        background: muted ? 'transparent' : '#EFF6FF',
        border: muted ? 'none' : '1px solid #BFDBFE',
        borderRadius: muted ? 0 : 6,
        ...extraStyle,
      }
    : {
        fontSize: 12,
        lineHeight: 1.5,
        color: '#1E3A8A',
        padding: '10px 14px',
        background: '#EFF6FF',
        border: '1px solid #BFDBFE',
        borderRadius: 8,
        marginBottom: 12,
        ...extraStyle,
      };

  return (
    <div style={baseStyle} role="note" aria-label="Inventory disclaimer">
      <strong style={{ marginRight: 4 }}>Stock estimate:</strong>{COPY}
    </div>
  );
}

// Two-tier policy → three semantic colors (in-stock/out-of-stock + unknown).
const BADGE_COLORS = {
  green: { bg: '#DCFCE7', border: '#86EFAC', text: '#166534' },
  red:   { bg: '#FEE2E2', border: '#FCA5A5', text: '#991B1B' },
  gray:  { bg: '#F3F4F6', border: '#D1D5DB', text: '#4B5563' },
};

/**
 * Small inventory pill: dot + label. Pass `inventory` as the object returned
 * by getInventoryDisplay() (client/src/lib/inventory.js). Renders nothing if
 * inventory is null (supplier doesn't support inventory).
 *
 * Props:
 *   inventory — { state, label, color, freshness, quantity } | null
 *   onRefreshClick — optional callback for an inline "refresh" link
 */
export function InventoryBadge({ inventory, onRefreshClick, style: extraStyle = {} }) {
  if (!inventory) return null;

  const c = BADGE_COLORS[inventory.color] || BADGE_COLORS.gray;
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 8px',
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.text,
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 500,
        ...extraStyle,
      }}
      title={inventory.freshness}
    >
      <span
        style={{
          width: 6, height: 6, borderRadius: '50%', background: c.text,
        }}
      />
      <span>{inventory.label}</span>
      {onRefreshClick && (
        <span
          onClick={(e) => { e.stopPropagation(); onRefreshClick(); }}
          style={{ marginLeft: 4, cursor: 'pointer', textDecoration: 'underline', opacity: 0.7 }}
        >
          refresh
        </span>
      )}
    </div>
  );
}

/**
 * Full inventory section for product detail modal and presentation editor.
 *
 * Renders badge + "Updated Xm ago" + "↻ Refresh stock" button. On click,
 * POSTs to /api/<supplier>/inventory/refresh which makes a live SOAP call
 * to the supplier and writes the result to the DB. Server enforces a 5-min
 * rate limit; we also show a disabled state with countdown when within
 * that window. On success we call `onRefreshed(updatedProduct)` so the
 * parent can update its product state without a separate fetch.
 *
 * Per-color breakdown: when `inventory_by_part` is available we render a
 * compact list of each color's stock. Manufactured / buy-to-order items
 * show "Made to order" instead of a zero.
 *
 * Props:
 *   product      — the product row (must include inventory_* fields, data_source, id)
 *   getToken     — async function returning an auth token (pass through from parent)
 *   onRefreshed  — optional (updatedProduct) => void after a successful refresh
 *   showPerColor — show the per-color list. Default true.
 *   style        — extra wrapper style
 */
export function InventoryPanel({ product, getToken, onRefreshed, showPerColor = true, style: extraStyle = {} }) {
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);
  const [justRefreshed, setJustRefreshed] = useState(false);

  // Recompute display whenever the product's inventory fields change.
  const inventory = useMemo(() => getInventoryDisplay(product), [
    product?.inventory_supports,
    product?.inventory_status,
    product?.inventory_quantity,
    product?.inventory_checked_at,
  ]);

  // Hide entirely if supplier doesn't expose inventory data at all.
  if (!inventory) return null;

  const refreshable    = canRefreshLive(product);
  const supplierPath   = supplierPathForRefresh(product);
  const productNativeId = nativeProductId(product);

  // Server enforces 5-min rate limit; we mirror it client-side so the button
  // shows a useful disabled state instead of getting a 429 on click.
  const RATE_LIMIT_SECONDS = 5 * 60;
  const lastCheckedMs = product?.inventory_checked_at ? new Date(product.inventory_checked_at).getTime() : null;
  const secondsSince  = lastCheckedMs ? Math.floor((Date.now() - lastCheckedMs) / 1000) : null;
  const rateLimited   = refreshable && secondsSince != null && secondsSince < RATE_LIMIT_SECONDS;
  const retrySeconds  = rateLimited ? RATE_LIMIT_SECONDS - secondsSince : 0;

  async function doRefresh() {
    if (!refreshable || !supplierPath || !productNativeId) return;
    setBusy(true);
    setError(null);
    try {
      const token = getToken ? await getToken() : null;
      const res = await fetch(`/api/${supplierPath}/inventory/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ productId: productNativeId }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      // Server returns updated inventory fields; merge them into the product
      // so the parent's local state stays in sync without re-fetching.
      if (onRefreshed && data.inventory) {
        onRefreshed({ ...product, ...data.inventory });
      }
      setJustRefreshed(true);
      setTimeout(() => setJustRefreshed(false), 3000);
    } catch (err) {
      setError(err.message || 'Refresh failed');
    } finally {
      setBusy(false);
    }
  }

  const buttonLabel = busy ? 'Refreshing…'
    : justRefreshed ? '✓ Updated'
    : rateLimited    ? `Wait ${Math.ceil(retrySeconds / 60)}m`
    : '↻ Refresh stock';

  const buttonDisabled = busy || !refreshable || rateLimited;
  const buttonTitle = !refreshable
    ? 'Live refresh available for direct-integration suppliers only'
    : rateLimited
      ? `Refreshed ${humanizeFreshness(product.inventory_checked_at)}. Try again in ~${Math.ceil(retrySeconds / 60)} min.`
      : 'Live check with supplier';

  return (
    <div style={{
      background: '#F8FAFC',
      border: '1px solid #E2E8F0',
      borderRadius: 9,
      padding: 12,
      marginBottom: 12,
      ...extraStyle,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <InventoryBadge inventory={inventory} />
        <span style={{ fontSize: 11, color: '#64748B' }}>
          Updated {inventory.freshness}
        </span>
        {/* Hide the button entirely while rate-limited — distributors found the
            "Wait 5m" pill noisy. The 3-second "✓ Updated" flash still shows
            after a successful refresh; once it clears we just disappear until
            the 5-min window passes. */}
        {(busy || justRefreshed || !rateLimited) && (
          <button
            onClick={doRefresh}
            disabled={buttonDisabled}
            title={buttonTitle}
            style={{
              marginLeft: 'auto',
              background: buttonDisabled ? '#F1F5F9' : '#fff',
              border: '1px solid #E2E8F0',
              borderRadius: 7,
              padding: '4px 12px',
              fontSize: 11,
              fontWeight: 500,
              color: buttonDisabled ? '#94A3B8' : '#0F172A',
              cursor: buttonDisabled ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}>
            {buttonLabel}
          </button>
        )}
      </div>

      {error && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#991B1B' }}>
          {error}
        </div>
      )}

      {showPerColor && Array.isArray(product?.inventory_by_part) && product.inventory_by_part.length > 0 && (() => {
        // SanMar (and others) return a part per color × size × warehouse, so the
        // raw list repeats each color many times. Aggregate by color and sum
        // availability so each color shows once, with the grand total.
        const byColor = {};
        for (const part of product.inventory_by_part) {
          const color = part.partColor || part.partId || 'Other';
          const avail = typeof part.available === 'number' ? part.available : 0;
          const mto   = part.manufacturedItem || part.buyToOrder;
          if (!byColor[color]) byColor[color] = { color, available: 0, madeToOrder: false };
          byColor[color].available += avail;
          if (mto) byColor[color].madeToOrder = true;
        }
        const colorRows  = Object.values(byColor).sort((a, b) => b.available - a.available);
        const grandTotal = colorRows.reduce((s, r) => s + r.available, 0);
        return (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #E2E8F0' }}>
            <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
              By color · {grandTotal.toLocaleString()} total across {colorRows.length} color{colorRows.length === 1 ? '' : 's'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {colorRows.map((row, i) => {
                const inStock    = row.available > 0 || row.madeToOrder;
                const pillColor  = inStock ? BADGE_COLORS.green : BADGE_COLORS.red;
                const stockLabel = row.madeToOrder ? 'Made to order'
                                 : row.available > 0 ? `${row.available.toLocaleString()} avail`
                                 : '0 avail';
                return (
                  <div key={row.color || i}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px',
                      background: pillColor.bg,
                      border: `1px solid ${pillColor.border}`,
                      color: pillColor.text,
                      borderRadius: 10, fontSize: 10, fontWeight: 500,
                    }}>
                    <strong>{row.color}</strong>
                    <span style={{ opacity: 0.75 }}>· {stockLabel}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {!refreshable && (
        <div style={{ marginTop: 8, fontSize: 10, color: '#94A3B8' }}>
          Live refresh available for direct-integration suppliers only.
        </div>
      )}
    </div>
  );
}

export default InventoryDisclaimer;
